'use client';

import { useTranslations } from 'next-intl';
import { createContext, useCallback, useContext, useState } from 'react';

import { ReviewVerifyDialog } from '@ui/components/features/review';
import { ApproveReviewConfirmDialog } from '@ui/components/features/review';
import { useUpdateReviewStatus } from '@website/hooks/useUpdateReviewStatus';

export interface ReviewVerifyData {
  reviewId: string;
  activityId: string;
  reviewerName: string;
  activityTitle: string;
  date: string;
  rating: number;
  comment: string;
  tags?: string[];
}

type ConfirmAction = 'approve' | 'reject' | null;

interface ReviewVerifyContextValue {
  openReviewVerify: (data: ReviewVerifyData) => void;
  closeReviewVerify: () => void;
  isOpen: boolean;
}

const ReviewVerifyContext = createContext<ReviewVerifyContextValue | null>(null);

export function useReviewVerify(): ReviewVerifyContextValue {
  const ctx = useContext(ReviewVerifyContext);
  if (!ctx) {
    throw new Error('useReviewVerify must be used within a ReviewVerifyProvider');
  }
  return ctx;
}

export function ReviewVerifyProvider({ children }: { children: React.ReactNode }) {
  const t = useTranslations('ReviewVerify');
  const [isOpen, setIsOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [reviewData, setReviewData] = useState<ReviewVerifyData | null>(null);
  const updateStatus = useUpdateReviewStatus();

  const openReviewVerify = useCallback((data: ReviewVerifyData) => {
    setReviewData(data);
    setIsOpen(true);
    setConfirmAction(null);
  }, []);

  const closeReviewVerify = useCallback(() => {
    setIsOpen(false);
    setConfirmAction(null);
    setReviewData(null);
  }, []);

  const handleApproveClick = useCallback(() => {
    setConfirmAction('approve');
  }, []);

  const handleRejectClick = useCallback(() => {
    setConfirmAction('reject');
  }, []);

  const handleConfirmAction = useCallback(() => {
    if (!reviewData || !confirmAction) return;
    updateStatus.mutate(
      {
        reviewId: reviewData.reviewId,
        status: confirmAction === 'approve' ? 'approved' : 'rejected',
        activityId: reviewData.activityId,
        previousStatus: 'pending',
        reviewRating: reviewData.rating
      },
      {
        onSuccess: () => {
          closeReviewVerify();
        }
      }
    );
  }, [reviewData, confirmAction, updateStatus, closeReviewVerify]);

  const handleCancelConfirm = useCallback(() => {
    setConfirmAction(null);
  }, []);

  const isConfirmOpen = isOpen && confirmAction !== null;
  const isReviewOpen = isOpen && confirmAction === null;

  // Confirmation dialog config based on action
  const confirmConfig =
    confirmAction === 'approve'
      ? {
          title: t('aboutToApprove'),
          description: t('approveDescription'),
          confirmLabel: t('yesApprove'),
          cancelLabel: t('noCheckOnce'),
          confirmVariant: 'success' as const
        }
      : {
          title: t('aboutToReject'),
          description: t('rejectDescription'),
          confirmLabel: t('yesReject'),
          cancelLabel: t('noCheckOnce'),
          confirmVariant: 'error' as const
        };

  return (
    <ReviewVerifyContext.Provider value={{ openReviewVerify, closeReviewVerify, isOpen }}>
      {children}
      {reviewData && (
        <>
          <ReviewVerifyDialog
            isOpen={isReviewOpen}
            onClose={closeReviewVerify}
            onApprove={handleApproveClick}
            onReject={handleRejectClick}
            userName={reviewData.reviewerName}
            activityTitle={reviewData.activityTitle}
            date={reviewData.date}
            rating={reviewData.rating}
            comment={reviewData.comment}
            tags={reviewData.tags}
            isSubmitting={updateStatus.isPending}
            title={t('customerExperience')}
            approveLabel={t('approveReview')}
            rejectLabel={t('rejectReview')}
            userLikedLabel={t('userLiked')}
          />
          <ApproveReviewConfirmDialog
            isOpen={isConfirmOpen}
            onClose={handleCancelConfirm}
            onConfirm={handleConfirmAction}
            isSubmitting={updateStatus.isPending}
            title={confirmConfig.title}
            description={confirmConfig.description}
            confirmLabel={confirmConfig.confirmLabel}
            cancelLabel={confirmConfig.cancelLabel}
            confirmButtonVariant={confirmConfig.confirmVariant}
          />
        </>
      )}
    </ReviewVerifyContext.Provider>
  );
}
